import { Translation } from 'primeng/api';

import { TolgeeService } from './tolgee.service';

/**
 * Builds the PrimeNG calendar/datepicker translation for the active language.
 * The button-bar labels ("Today"/"Clear") come from Tolgee; the day/month names,
 * week start and date format are derived from the platform `Intl` data for the
 * matching locale — locale data, not app copy, so nothing is hardcoded.
 */
export function buildPrimeNgTranslation(tolgee: TolgeeService, lang: string): Translation {
    const locale = tolgee.getAngularDateLocale(lang);

    // Reuse five formatters across the loops instead of allocating one per iteration.
    const monthLong = new Intl.DateTimeFormat(locale, { month: 'long' });
    const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' });
    const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long' });
    const weekdayShort = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    const weekdayNarrow = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });

    const monthNames: string[] = [];
    const monthNamesShort: string[] = [];
    for (let m = 0; m < 12; m++) {
        const d = new Date(2021, m, 1);
        monthNames.push(monthLong.format(d));
        monthNamesShort.push(monthShort.format(d));
    }

    const dayNames: string[] = [];
    const dayNamesShort: string[] = [];
    const dayNamesMin: string[] = [];
    // 2021-08-01 is a Sunday → PrimeNG expects Sunday-first weekday arrays.
    for (let d = 0; d < 7; d++) {
        const date = new Date(2021, 7, 1 + d);
        dayNames.push(weekdayLong.format(date));
        dayNamesShort.push(weekdayShort.format(date));
        dayNamesMin.push(weekdayNarrow.format(date));
    }

    return {
        today: tolgee.translate('common.datePicker.today', undefined, 'Heute'),
        clear: tolgee.translate('common.datePicker.clear', undefined, 'Löschen'),
        // Kept fixed (not locale-derived) on purpose: every picker in the app renders
        // `dd.mm.yy` and its keyboard mask (`u2bDateMask`) parses `dd.mm.yyyy`, so a
        // locale-specific format here would desync the mask and break typed input.
        dateFormat: 'dd.mm.yy',
        firstDayOfWeek: locale.startsWith('en') ? 0 : 1,
        dayNames,
        dayNamesShort,
        dayNamesMin,
        monthNames,
        monthNamesShort,
    };
}
